import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa";
import { XIcon } from "react-share";
import Styles from "./WalkToTheBoardEvent.module.scss";
import {
  getWalkToTheBoardEventImageUrl,
  WALK_TO_BOARD_CITIES,
  WALK_TO_BOARD_REGISTRATION_URL_COMMUNITY,
  WALK_TO_BOARD_REGISTRATION_URL_NON_COMMUNITY,
} from "../constants/walkToTheBoardEvent";
import { urls } from "../utils/urls";

const EVENT_IMAGE = getWalkToTheBoardEventImageUrl();

export default function WalkToTheBoardEvent() {
  return (
    <div className={Styles.pageWrapper}>
      <Helmet>
        <title>Walk to the Board | Iron Lady</title>
        <meta
          name="description"
          content="Walk to the Board — a pan-India leadership experience for women ready to rise, connect, and lead across 7 cities. Sunday 19 April 2026."
        />
        <link rel="canonical" href="https://iamironlady.com/events/walk-to-the-board" />
      </Helmet>

      <section className={Styles.hero}>
        <div className={Styles.heroShell}>
          <nav className={Styles.heroBreadcrumb} aria-label="Breadcrumb">
            <Link to="/" className={Styles.heroBreadcrumbLink}>
              Home
            </Link>
            <span className={Styles.heroBreadcrumbSep}>/</span>
            <Link to="/events" className={Styles.heroBreadcrumbLink}>
              Events
            </Link>
            <span className={Styles.heroBreadcrumbSep}>/</span>
            <span className={Styles.heroBreadcrumbCurrent}>Walk to the Board</span>
          </nav>
          <div className={Styles.heroSplit}>
            <div className={Styles.heroTextCol}>
              <div className={Styles.heroContent}>
                <div className={Styles.heroEyebrow}>
                  A national leadership movement
                </div>
                <div className={Styles.heroBrand}>Walk to the Board</div>
                <div className={Styles.heroTagline}>
                  <span className={Styles.heroTaglineLead}>
                    Step into visibility
                  </span>{" "}
                  <span className={Styles.heroTaglineAccent}>
                    Confidence & influence.
                  </span>
                </div>
                <div className={Styles.heroMeta}>
                  <div className={Styles.heroMetaBlock}>
                    <span className={Styles.heroMetaLabel}>Time</span>
                    <span
                      className={`${Styles.heroMetaValue} ${Styles.heroMetaValueTime}`}
                    >
                      {`9:00 AM – 12:00 PM\u00A0IST`}
                    </span>
                  </div>
                  <div className={Styles.heroMetaBlock}>
                    <span className={Styles.heroMetaLabel}>Date</span>
                    <span className={Styles.heroMetaValue}>
                      Sunday, 19 April 2026
                    </span>
                  </div>
                </div>
                <p className={Styles.heroSub}>
                  A pan-India leadership experience for women ready to rise,
                  connect, and lead across 7 cities, on one powerful stage.
                </p>
                <ul
                  className={Styles.heroCitiesList}
                  aria-label="Host cities"
                >
                  {WALK_TO_BOARD_CITIES.map((city) => (
                    <li key={city} className={Styles.heroCityChip}>
                      {city}
                    </li>
                  ))}
                </ul>
                <div className={Styles.heroCta}>
                  <div className={Styles.heroCtaGroup}>
                    <a
                      href={WALK_TO_BOARD_REGISTRATION_URL_NON_COMMUNITY}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={Styles.btnPrimary}
                    >
                      <span>Register now</span> →
                    </a>
                    <Link
                      to="/100-board-members"
                      className={Styles.btnOutline}
                    >
                      <span>100 Board Members</span> →
                    </Link>
                  </div>
                </div>
                <div
                  className={Styles.heroSocial}
                  aria-label="Iron Lady on social media"
                >
                  <a
                    href={urls.ilTwitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.heroSocialLink}
                    aria-label="Iron Lady on X"
                  >
                    <XIcon size={17} />
                  </a>
                  <a
                    href={urls.ilFacebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.heroSocialLink}
                    aria-label="Iron Lady on Facebook"
                  >
                    <FaFacebookF aria-hidden />
                  </a>
                  <a
                    href={urls.ilLinkedon}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.heroSocialLink}
                    aria-label="Iron Lady on LinkedIn"
                  >
                    <FaLinkedinIn aria-hidden />
                  </a>
                  <a
                    href={urls.ilInstagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.heroSocialLink}
                    aria-label="Iron Lady on Instagram"
                  >
                    <FaInstagram aria-hidden />
                  </a>
                  <a
                    href={urls.ilYoutube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.heroSocialLink}
                    aria-label="Iron Lady on YouTube"
                  >
                    <FaYoutube aria-hidden />
                  </a>
                </div>
              </div>
            </div>
            <div className={Styles.heroImageCol}>
              <div className={Styles.heroImageFrame}>
                <img
                  src={EVENT_IMAGE}
                  alt="Walk to the Board — event artwork"
                  className={Styles.heroArtImg}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={Styles.sectionOverview}>
        <div className={Styles.sectionNarrow}>
          <span className={Styles.eyebrow}>Overview</span>
          <h2 className={Styles.sectionTitleLeft}>Leadership that shows up</h2>
          <div className={Styles.prose}>
            <p>
              Iron Lady stands as the leading leadership training platform for
              women in India. With an impressive track record, we have empowered
              over 1 lakh women, enabling them to create substantial positive
              changes in both their personal and professional spheres.
            </p>
            <p>
              Walk to the Board is a nationwide leadership experience designed
              for women who are ready to step into visibility, confidence, and
              influence.
            </p>
            <p>
              Bringing together aspiring and established women leaders, the
              initiative blends a unique leadership ramp walk with powerful panel
              conversations and meaningful networking, creating a space where
              women don&apos;t just talk about leadership, but actively step
              into it.
            </p>
            <p>
              With a dynamic audience of community members, senior leaders, and
              professionals, Walk to the Board offers a rare opportunity to be
              seen, heard, and connected in a high-impact environment.
            </p>
          </div>
        </div>
      </section>

      <section className={Styles.sectionHighlights}>
        <div className={Styles.sectionHeader}>
          <span className={Styles.eyebrow}>Highlights</span>
          <h2>What you&apos;ll take away</h2>
        </div>
        <ul className={Styles.highlightList}>
          <li>
            Witness the growth journeys of senior women leaders.
          </li>
          <li>
            Learn directly from success stories of women who have become CEOs,
            CHROs, CFOs, and more — paving the way for future generations.
          </li>
          <li>
            Understand how others have dealt with challenges and fast-tracked
            their careers through the{" "}
            <Link to="/100-board-members" className={Styles.inlineLinkDark}>
              100 Board Members Initiative
            </Link>
            .
          </li>
          <li>
            Network with purpose — meet peers, senior leaders, and changemakers;
            exchange ideas and build relationships that can extend well beyond
            the event.
          </li>
          <li>
            Get energized to step into your next level of leadership.
          </li>
        </ul>
      </section>

      <section className={Styles.sectionFlow}>
        <div className={Styles.sectionNarrow}>
          <h2 className={Styles.sectionTitleLeft}>Event flow & experience</h2>
          <div className={Styles.prose}>
            <p>
              The event begins at 9:00 AM with a powerful national Zoom kickoff,
              connecting all participating cities in a shared moment of energy and
              intent. This is followed by the signature leadership ramp walk and
              insightful panel discussions, creating a dynamic blend of action
              and conversation.
            </p>
            <p>
              The experience concludes at 12:00 PM, with light refreshments
              served for all participants.
            </p>
          </div>
        </div>
      </section>

      <section className={Styles.sectionWhoDress}>
        <div className={Styles.whoDressGrid}>
          <div className={Styles.whoDressCard}>
            <span className={Styles.eyebrow}>Eligibility</span>
            <h3 className={Styles.whoDressTitle}>Who can participate</h3>
            <p className={Styles.whoDressText}>
              Women aged 25+ with professional experience who are ready to
              experience growth, meaningful connections, and leadership
              opportunities.
            </p>
          </div>
          <div className={Styles.whoDressCard}>
            <span className={Styles.eyebrow}>On the day</span>
            <h3 className={Styles.whoDressTitle}>Dress code</h3>
            <p className={Styles.whoDressText}>
              Red or Black professional attire / saree (preferred, not mandatory).
              Wear whatever feels professional and comfortable for you.
              The day is built for networking: connect with peers and leaders,
              exchange ideas, and build relationships in a welcoming space.
            </p>
          </div>
        </div>
      </section>

      <section className={Styles.sectionDetails}>
        <div className={Styles.detailsInner}>
          <h2>Event details</h2>
          <p className={Styles.detailsIntro}>
            Sunday, 19 April 2026 · 9:00 AM – 12:00 PM IST
          </p>

          <div className={Styles.scheduleRow}>
            <div className={Styles.scheduleCell}>
              <span className={Styles.scheduleLabel}>Date</span>
              <span className={Styles.scheduleValue}>Sunday, 19 April 2026</span>
            </div>
            <div className={Styles.scheduleCell}>
              <span className={Styles.scheduleLabel}>Time</span>
              <span className={Styles.scheduleValue}>9:00 AM – 12:00 PM IST</span>
            </div>
          </div>

          <div className={Styles.citiesPanel}>
            <p className={Styles.citiesHeadline}>
              7 cities. One movement. Join from yours.
            </p>
            <ul className={Styles.cityList} aria-label="Host cities">
              {WALK_TO_BOARD_CITIES.map((city) => (
                <li key={city} className={Styles.cityTag}>
                  {city}
                </li>
              ))}
            </ul>
            <p className={Styles.citiesNote}>
              Hosted in these cities only — select yours when you register.
            </p>
          </div>

          <h3 className={Styles.regHeading}>Registration</h3>
          <p className={Styles.regLead}>
            To reserve a seat, choose the option that applies to you. Checkout
            opens in a new tab.
          </p>
          <div className={Styles.regGrid}>
            <a
              href={WALK_TO_BOARD_REGISTRATION_URL_COMMUNITY}
              target="_blank"
              rel="noopener noreferrer"
              className={Styles.btnPrimary}
            >
              <span>Community members</span> →
            </a>
            <a
              href={WALK_TO_BOARD_REGISTRATION_URL_NON_COMMUNITY}
              target="_blank"
              rel="noopener noreferrer"
              className={Styles.btnSecondary}
            >
              <span>Non-community participants</span> →
            </a>
          </div>
          <p className={Styles.footnote}>Secure payment via Razorpay.</p>
        </div>
      </section>
    </div>
  );
}
